// components/ui/buttons/button.tsx
import React from 'react'
import { Button as MuiButton, CircularProgress } from '@material-ui/core'

export interface ButtonProps {
  variant?: 'contained' | 'outlined' | 'text' | 'danger'
  | 'gold' | 'tonal' | 'outline' | 'primary' | 'ghost' // legacy aliases
  size?: 'small' | 'medium' | 'large' | 'sm' | 'md' | 'lg' // legacy aliases
  fullWidth?: boolean
  icon?: React.ReactNode
  rightIcon?: React.ReactNode
  loading?: boolean
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  type?: 'button' | 'submit' | 'reset'
  autoFocus?: boolean
}

const BASE_FONT: React.CSSProperties = {
  fontFamily: "'proxima-nova', sans-serif",
  fontWeight: 700,
  letterSpacing: '0.02857em',
  textTransform: 'uppercase',
  paddingLeft: 20,
  paddingRight: 20,
  fontSize: '13px',
  minHeight: '36px',
}

const DANGER_STYLE: React.CSSProperties = {
  backgroundColor: 'rgb(var(--pcg-danger))',
  color: 'rgb(var(--pcg-fg))',
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'contained',
    size = 'medium',
    fullWidth = false,
    icon,
    rightIcon,
    loading = false,
    disabled,
    onClick,
    className,
    style,
    children,
    type = 'button',
    autoFocus,
  }, ref) => {
    const variantMap: Record<string, 'contained' | 'outlined' | 'text'> = {
      contained: 'contained', gold: 'contained', tonal: 'contained', primary: 'contained',
      outlined: 'outlined', outline: 'outlined',
      text: 'text', ghost: 'text',
      danger: 'contained',
    }
    const sizeMap: Record<string, 'small' | 'medium' | 'large'> = {
      small: 'small', sm: 'small',
      medium: 'medium', md: 'medium',
      large: 'large', lg: 'large',
    }
    const isDanger = variant === 'danger'
    const muiVariant = variantMap[variant ?? 'contained']
    const muiSize = sizeMap[size ?? 'medium']

    const isDisabled = disabled || loading;

    const isSmall = muiSize === 'small';

    return (
      <MuiButton
        ref={ref as any}
        variant={muiVariant}
        color={isDanger ? undefined : 'primary'}
        size={muiSize}
        fullWidth={fullWidth}
        disabled={false}
        aria-disabled={isDisabled || undefined}
        onClick={isDisabled ? undefined : onClick}
        type={type}
        autoFocus={autoFocus}
        className={className}
        startIcon={!loading && icon ? icon : undefined}
        endIcon={!loading && rightIcon ? rightIcon : undefined}
        style={{
          ...BASE_FONT,
          ...(isSmall ? { minHeight: '30px', paddingLeft: 12, paddingRight: 12, fontSize: '11px' } : {}),
          ...(isDanger ? DANGER_STYLE : {}),
          ...(isDisabled ? { opacity: 0.3, pointerEvents: 'none' } : {}),
          ...style,
        }}
      >
        {loading && (
          <CircularProgress
            size={16}
            thickness={5}
            style={{ color: 'currentColor', marginRight: children ? 8 : 0 }}
          />
        )}
        {children}
      </MuiButton>
    )
  }
)

Button.displayName = 'Button'
